import { computed } from 'vue';
import { TableId } from '@/modules/common/enums/table-id.enum.ts';
import { useAbort } from '@/modules/common/composables/use-abort.ts';
import { toCommentPayload } from '@/modules/common/utils/comment-markdown.util.ts';
import { SystemApi } from '@/modules/core/api/system.api.ts';
import AddressApi from '@/modules/counterparty/api/address-api.ts';
import BankDetailsApi from '@/modules/counterparty/api/bank-details-api.ts';
import ContactApi from '@/modules/counterparty/api/contact-api.ts';
import { CounterpartyApi } from '@/modules/counterparty/api/counterparty.api.ts';
import { CounterpartyStepCode } from '@/modules/counterparty/counterparty-master/enums/step-code.ts';
import { useCounterpartyMaster } from '@/modules/counterparty/counterparty-master/counterparty-master.ts';
import { useCounterpartyMasterAccess } from '@/modules/counterparty/counterparty-master/composables/use-counterparty-master-access.ts';
import { LegalFormQueryOptions } from '@/modules/counterparty/query-options/legal-form.query-options.ts';
import { CategoryQueryOptions } from '@/modules/counterparty/query-options/category.query-options.ts';
import type { Option } from '@/modules/common/types/option.type.ts';
import { queryClient } from '@/tanstack-query.ts';
import type { SubmitTask } from '@/modules/document/document-master/types/submit-task.ts';
import { refreshCounterpartiesList } from '@/modules/counterparty/counterparty-master/utils/refresh-counterparties-list.util.ts';
import { resolveMasterDirectoryId } from '@/modules/counterparty/counterparty-master/utils/resolve-master-directory.util.ts';
import { CounterpartyExecutionTaskCode } from '@/modules/counterparty/counterparty-master/enums/execution-task-code.ts';

function encodeParentIds(ids: number[]) {
  if (!ids.length) {
    return undefined;
  }

  return `#${ids.join('##')}#`;
}

async function resolveLegalFormId(legalForm: Option | null, signal?: AbortSignal) {
  if (!legalForm) {
    return undefined;
  }

  if (typeof legalForm.code === 'number') {
    return legalForm.code;
  }

  const forms = await queryClient.ensureQueryData(LegalFormQueryOptions.getList(signal));
  return forms.find((form) => form.name === legalForm.label || form.name === String(legalForm.code))?.id;
}

async function resolveDefaultCategory(signal?: AbortSignal) {
  const categories = await queryClient.ensureQueryData(CategoryQueryOptions.getList(signal));
  const name = categories[0]?.name;

  if (!name) {
    throw new Error('The counterparty category is not defined.');
  }

  return name;
}

export const COUNTERPARTY_TASK_STEP: Partial<Record<string, CounterpartyStepCode>> = {
  [CounterpartyExecutionTaskCode.Card]: CounterpartyStepCode.Manager,
  [CounterpartyExecutionTaskCode.Addresses]: CounterpartyStepCode.Address,
  [CounterpartyExecutionTaskCode.Contacts]: CounterpartyStepCode.Contact,
  [CounterpartyExecutionTaskCode.BankDetails]: CounterpartyStepCode.BankDetails,
  [CounterpartyExecutionTaskCode.Comment]: CounterpartyStepCode.Comment,
};

function isSubmitTask<T>(task: T): task is Exclude<T, false> {
  return task !== false;
}

export function useCounterpartyExecutionTasks() {
  const { signal } = useAbort();
  const { formData, createdId, setCreatedId, completedTaskCodes, submittedItemIds } = useCounterpartyMaster();
  const masterAccess = useCounterpartyMasterAccess();

  function once(code: CounterpartyExecutionTaskCode, handler: () => Promise<void>) {
    return async () => {
      if (completedTaskCodes.has(code)) {
        return;
      }

      await handler();
      completedTaskCodes.add(code);
    };
  }

  async function addPendingItems<T extends { id: string }>(items: T[], add: (item: T) => Promise<void>) {
    await Promise.all(
      items.map(async (item) => {
        if (submittedItemIds.has(item.id)) {
          return;
        }

        await add(item);
        submittedItemIds.add(item.id);
      })
    );
  }

  async function addCard() {
    if (createdId.value !== null) {
      return;
    }

    const data = formData.value;
    const access = masterAccess.value;

    if (!data.type || !data.manager) {
      throw new Error('The counterparty type or manager is not defined.');
    }

    if (typeof data.region?.code !== 'number' || typeof data.segment?.code !== 'number') {
      throw new Error('The counterparty region or segment is not defined.');
    }

    const segmentId = await resolveMasterDirectoryId('segment', data.segment, signal.value);
    formData.value = {
      ...formData.value,
      segment: { code: segmentId, label: data.segment.label },
    };

    const regionId = await resolveMasterDirectoryId('region', formData.value.region, signal.value);
    formData.value = {
      ...formData.value,
      region: { code: regionId, label: data.region.label },
    };

    const id = await CounterpartyApi.add(
      {
        typeId: data.type.code,
        name: data.name.trim(),
        managerId: data.manager.code,
        regionId,
        segmentId,
        category: await resolveDefaultCategory(signal.value),
        parents:
          access.subordination && data.sections.subordination && data.isSubordinated
            ? encodeParentIds(data.parents.map((parent) => Number(parent.id)))
            : undefined,
        inn: data.inn.trim() || undefined,
        kpp: data.kpp.trim() || undefined,
        okato: data.okato.trim() || undefined,
        okpo: data.okpo.trim() || undefined,
        ogrn: data.ogrn.trim() || undefined,
        legalFormId: await resolveLegalFormId(data.legalForm, signal.value),
        city: access.city ? data.city?.label || data.city?.code || undefined : undefined,
        code: access.code ? data.code.trim() || undefined : undefined,
      },
      signal.value
    );

    setCreatedId(id);
    void refreshCounterpartiesList({ created: true });
  }

  function requiredCreatedId() {
    if (createdId.value === null) {
      throw new Error('The counterparty id is not defined.');
    }

    return createdId.value;
  }

  const tasks = computed((): SubmitTask<CounterpartyExecutionTaskCode>[] => {
    const data = formData.value;
    const access = masterAccess.value;

    return (
      [
        {
          code: CounterpartyExecutionTaskCode.Card,
          title: 'counterparty.master.steps.execution.step.card',
          handler: once(CounterpartyExecutionTaskCode.Card, addCard),
          required: true,
        },
        access.addresses &&
          data.sections.addresses &&
          !!data.addresses.length && {
            code: CounterpartyExecutionTaskCode.Addresses,
            title: 'counterparty.master.steps.execution.step.addresses',
            required: true,
            handler: async () => {
              const parentId = requiredCreatedId();

              await addPendingItems(formData.value.addresses, (item) =>
                AddressApi.add({
                  parentId,
                  formData: {
                    type: item.type,
                    address: item.address,
                    comment: item.comment,
                  },
                })
              );
            },
          },
        access.contacts &&
          data.sections.contacts &&
          !!data.contacts.length && {
            code: CounterpartyExecutionTaskCode.Contacts,
            title: 'counterparty.master.steps.execution.step.contacts',
            required: true,
            handler: async () => {
              const parentId = requiredCreatedId();

              await addPendingItems(formData.value.contacts, (item) =>
                ContactApi.addExt({
                  parentId,
                  formData: {
                    type: item.type,
                    contact: item.contact,
                    comment: item.comment,
                    fio: item.fio,
                    position: item.position?.label,
                    birthDay: item.birthDay,
                    mask: item.mask,
                  },
                })
              );
            },
          },
        access.bankDetails &&
          data.sections.bankDetails &&
          !!data.bankDetails.length && {
            code: CounterpartyExecutionTaskCode.BankDetails,
            title: 'counterparty.master.steps.execution.step.bankDetails',
            required: true,
            handler: async () => {
              const parentId = requiredCreatedId();

              await addPendingItems(formData.value.bankDetails, (item) =>
                BankDetailsApi.add({
                  parentId,
                  formData: {
                    bank: item.bank,
                    bankIdentificationCode: item.bankIdentificationCode,
                    correspondentAccount: item.correspondentAccount,
                    currentAccount: item.currentAccount,
                    comment: item.comment,
                  },
                })
              );
            },
          },
        access.comment &&
          data.sections.comment &&
          !!data.comment.trim() && {
            code: CounterpartyExecutionTaskCode.Comment,
            title: 'counterparty.master.steps.execution.step.comment',
            required: true,
            handler: once(CounterpartyExecutionTaskCode.Comment, async () => {
              const { comment, commentMarkdown } = toCommentPayload(formData.value.comment);

              await SystemApi.addComment(
                {
                  tableId: TableId.Counterparties,
                  parentId: requiredCreatedId(),
                  comment,
                  commentMarkdown,
                },
                signal.value
              );
            }),
          },
      ] satisfies (false | SubmitTask<CounterpartyExecutionTaskCode>)[]
    ).filter(isSubmitTask);
  });

  return tasks;
}
